import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// Bump when window.__siteSettings is updated so all mounted SEO components re-render.
let _settingsTick = 0;
export function bumpSeoSettings() { _settingsTick++; window.dispatchEvent(new Event("seo-settings")); }

/**
 * Lightweight SEO head manager — no extra dependencies.
 * Sets <title>, meta description/keywords/robots, canonical, OG/Twitter tags,
 * locale, and an optional JSON-LD structured-data script.
 *
 * Always reads global defaults from window.__siteSettings (populated by App.jsx).
 */
export default function SEO({
  title,
  description,
  keywords,
  image,
  type = "website",
  canonical,
  jsonLd,
  noindex = false,
}) {
  const location = useLocation();
  const [tick, setTick] = useState(_settingsTick);

  useEffect(() => {
    const h = () => setTick((t) => t + 1);
    window.addEventListener("seo-settings", h);
    return () => window.removeEventListener("seo-settings", h);
  }, []);

  useEffect(() => {
    const settings = window.__siteSettings || {};
    const siteName = settings.seo_site_name || "Správný Směr";
    const defaultTitle = settings.seo_default_title || siteName;
    const defaultDesc = settings.seo_default_description || "";
    const defaultKw = settings.seo_default_keywords || "";
    const defaultImg = settings.seo_og_image || "";
    const rawBase = settings.seo_canonical_base || window.location.origin;
    const base = (/^https?:\/\//i.test(rawBase) ? rawBase : "https://" + String(rawBase).replace(/^\/+/, "")).replace(/\/$/, "");
    const locale = settings.seo_locale || "cs_CZ";
    const robotsDirective = noindex ? "noindex, nofollow" : (settings.seo_robots || "index, follow");
    const twitterHandle = settings.seo_twitter_handle || "";

    const finalTitle = title ? (title.toLowerCase().includes(siteName.toLowerCase()) ? title : `${title} | ${siteName}`) : defaultTitle;
    const finalDesc = description || defaultDesc;
    const finalKw = keywords || defaultKw;
    const finalImg = image || defaultImg;
    const finalCanonical = canonical || `${base}${location.pathname}`;

    document.title = finalTitle;
    document.documentElement.setAttribute("lang", locale.split("_")[0] || "cs");

    setMeta("name", "description", finalDesc);
    setMeta("name", "keywords", finalKw);
    setMeta("name", "robots", robotsDirective);
    setMeta("name", "author", siteName);
    setMeta("name", "theme-color", "#2563eb");

    // Open Graph
    setMeta("property", "og:type", type);
    setMeta("property", "og:site_name", siteName);
    setMeta("property", "og:title", finalTitle);
    setMeta("property", "og:description", finalDesc);
    setMeta("property", "og:url", finalCanonical);
    setMeta("property", "og:locale", locale);
    if (finalImg) {
      setMeta("property", "og:image", finalImg);
      setMeta("property", "og:image:alt", finalTitle);
    }

    // Twitter
    setMeta("name", "twitter:card", finalImg ? "summary_large_image" : "summary");
    setMeta("name", "twitter:title", finalTitle);
    setMeta("name", "twitter:description", finalDesc);
    if (finalImg) setMeta("name", "twitter:image", finalImg);
    if (twitterHandle) setMeta("name", "twitter:site", twitterHandle);

    // Canonical link
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", finalCanonical);

    // JSON-LD structured data (per-page) — supports single object or array of objects
    document.querySelectorAll("script[data-page-jsonld]").forEach((n) => n.remove());
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.filter(Boolean).forEach((obj, idx) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-page-jsonld", String(idx));
        s.text = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }
  }, [title, description, keywords, image, type, canonical, jsonLd, noindex, location.pathname, tick]);

  return null;
}

function setMeta(attr, name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
